import { FormattedMessage, useIntl } from 'react-intl';
import { FC, useCallback, useState, useEffect } from 'react';
import { FormSection } from '../FormSection';
import { toast } from '@/hooks/useToast';
import {
  getGetOrganizationByIDQueryKey,
  getListOrganizationsQueryKey,
  OrganizationDTO,
  usePatchOrganizationState,
} from '@/api/trafikportalen';
import { useQueryClient } from '@tanstack/react-query';
import Button from '../core/Button';
import { WarningDialog } from '../WarningDialog';
import {
  isOrganizationInactivating,
  getRemainingInactivationTime,
} from '@/helpers/organizationStatus';
import { IconWarningTriangle } from '@/icons';

interface OrganizationAdvancedProps {
  organization: OrganizationDTO;
}

export const OrganizationAdvanced: FC<OrganizationAdvancedProps> = ({
  organization,
}) => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const patchOrganizationState = usePatchOrganizationState();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingState, setPendingState] = useState<
    'inactive' | 'approved' | null
  >(null);
  const [remainingTime, setRemainingTime] = useState(0);

  const isActive = organization.state === 'approved';
  const inactivating = isOrganizationInactivating(organization);

  useEffect(() => {
    if (!inactivating) {
      setRemainingTime(0);
      return;
    }

    const updateRemainingTime = () => {
      const timeRemaining = getRemainingInactivationTime(organization);
      setRemainingTime(timeRemaining);

      if (timeRemaining <= 0) {
        queryClient.invalidateQueries({
          queryKey: getGetOrganizationByIDQueryKey(organization.id),
        });
        queryClient.invalidateQueries({
          queryKey: getListOrganizationsQueryKey(),
        });
      }
    };

    updateRemainingTime();
    const interval = setInterval(updateRemainingTime, 60 * 1000);

    return () => clearInterval(interval);
  }, [inactivating, organization, queryClient]);

  const handleStateChange = useCallback((newState: 'inactive' | 'approved') => {
    setPendingState(newState);
    setShowConfirmDialog(true);
  }, []);

  const confirmStateChange = useCallback(() => {
    if (!pendingState) return;

    patchOrganizationState.mutate(
      {
        organizationID: organization.id,
        data: { state: pendingState },
      },
      {
        onSuccess: async () => {
          toast.success(
            {
              title:
                pendingState === 'inactive'
                  ? formatMessage({
                      defaultMessage: 'Organisationen inaktiverades',
                    })
                  : formatMessage({
                      defaultMessage: 'Organisationen aktiverades',
                    }),
            },
            { autoClose: 3000 },
          );
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: getGetOrganizationByIDQueryKey(organization.id),
            }),
            queryClient.invalidateQueries({
              queryKey: getListOrganizationsQueryKey(),
            }),
          ]);
          setShowConfirmDialog(false);
          setPendingState(null);
        },
        onError: (error) => {
          console.error(error);
          toast.error(
            {
              title: formatMessage({
                defaultMessage: 'Kunde inte uppdatera organisationen',
              }),
              description: error.message,
            },
            { autoClose: 3000 },
          );
          setShowConfirmDialog(false);
          setPendingState(null);
        },
      },
    );
  }, [
    pendingState,
    patchOrganizationState,
    organization.id,
    queryClient,
    formatMessage,
  ]);

  const remainingMinutes = (ms: number): number => {
    return Math.floor(ms / 60000) + 1;
  };

  return (
    <>
      <div className="flex w-full flex-col gap-4">
        {inactivating && (
          <div className="flex items-start gap-3 rounded-md border border-yellow-600 bg-yellow-50 p-4">
            <IconWarningTriangle className="size-5 text-yellow-600" />
            <div className="flex-1">
              <b className="text-yellow-800">
                <FormattedMessage defaultMessage="Organisationen inaktiveras" />
              </b>
              <p className="mt-2 text-yellow-700">
                <FormattedMessage
                  defaultMessage="Inaktiveringen pågår och kommer att vara klar om ca {time} minuter. Under denna tid avslutas alla aktiva/oanvända biljetter, tjänsteresor i app och tjänsteresor på kort."
                  values={{
                    time: (
                      <span className="font-medium">
                        {remainingMinutes(remainingTime)}
                      </span>
                    ),
                  }}
                />
              </p>
            </div>
          </div>
        )}

        <FormSection
          title={
            isActive ? (
              <FormattedMessage defaultMessage="Inaktivera" />
            ) : (
              <FormattedMessage defaultMessage="Aktivera" />
            )
          }
          description={
            isActive ? (
              <FormattedMessage defaultMessage="Samtliga aktiva/oanvända biljetter, tjänsteresa i app och tjänstresa på kort avslutas (och återställs inte automatiskt vid eventuell återaktivering). Varken systemadministratörer eller de ordinarie organisationsadministratörerna kommer kunna komma åt organisationen. Hela inaktiveringen tar ca 15 minuter att genomföra." />
            ) : (
              <FormattedMessage defaultMessage="Administratörer kommer åter få tillgång till organisationen men biljetter och tjänsteresor som var upplagda tidigare blir inte återställda utan det måste göras manuellt." />
            )
          }
          editMode={false}
        >
          <div className="flex flex-col gap-2">
            {isActive && (
              <Button
                variant="outline"
                color="red"
                onClick={() => handleStateChange('inactive')}
                disabled={patchOrganizationState.isPending || inactivating}
              >
                <FormattedMessage defaultMessage="Inaktivera organisation" />
              </Button>
            )}
            {!isActive && !inactivating && (
              <Button
                variant="outline"
                onClick={() => handleStateChange('approved')}
                disabled={patchOrganizationState.isPending}
              >
                <FormattedMessage defaultMessage="Aktivera organisation" />
              </Button>
            )}
          </div>
        </FormSection>
      </div>

      <WarningDialog
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        title={
          pendingState === 'inactive'
            ? formatMessage({ defaultMessage: 'Inaktivera organisation' })
            : formatMessage({
                defaultMessage: 'Aktivera organisation',
              })
        }
        content={
          <p>
            {pendingState === 'inactive'
              ? formatMessage({
                  defaultMessage:
                    'Är du säker på att du vill inaktivera denna organisation? ',
                })
              : formatMessage({
                  defaultMessage:
                    'Är du säker på att du vill aktivera denna organisation?',
                })}
          </p>
        }
        onCancel={() => {
          setShowConfirmDialog(false);
          setPendingState(null);
        }}
        onConfirm={confirmStateChange}
        buttonText={
          pendingState === 'inactive'
            ? formatMessage({ defaultMessage: 'Inaktivera' })
            : formatMessage({ defaultMessage: 'Aktivera' })
        }
        loading={patchOrganizationState.isPending}
      />
    </>
  );
};
